/**
 * One git server's auth controls.
 *
 * `auth_method === 'oauth'` (a login provider is attached to the server)
 * gets Connect/Disconnect and no token field; `login_connected` picks
 * between them. `'pat'`/`'none'` have no provider attached at all — there
 * is nothing to connect to — so both keep the token form, with `has_token`
 * driving the "Add" vs "Update"/"Remove" labels as before.
 */

import { type ReactElement, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDisconnectLogin } from '@features/logins/hooks/useLogins';
import {
  useDeleteGitCredential,
  useSetGitCredential,
} from '../hooks/useGitCredentials';
import { useConnectGitServer } from '../hooks/useConnectGitServer';
import type { GitCredential } from '../types/git.types';

export function GitServerAuthRow({ credential: c }: { credential: GitCredential }): ReactElement {
  const set = useSetGitCredential();
  const del = useDeleteGitCredential();
  const disconnect = useDisconnectLogin();
  const { connect, isConnecting, error: connectError } = useConnectGitServer();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [token, setToken] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);

  const submit = async (): Promise<void> => {
    setError(null);
    try {
      await set.mutateAsync({
        serverId: c.server_id,
        body: {
          ...(token && { token }),
          ...(name && { git_user_name: name }),
          ...(email && { git_user_email: email }),
        },
      });
      handleCancel();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save credential');
    }
  };

  const handleEdit = (): void => {
    setEditing(true);
    setToken('');
    setName(c.git_user_name ?? '');
    setEmail(c.git_user_email ?? '');
    setError(null);
    setShowToken(false);
  };

  const handleCancel = (): void => {
    setEditing(false);
    setToken('');
    setName('');
    setEmail('');
    setError(null);
    setShowToken(false);
  };

  const handleDisconnect = (): void => {
    if (!c.login_provider_name) return;
    disconnect.mutate(c.login_provider_name, {
      onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['gitCredentials'] }); },
    });
  };

  const rowError = connectError
    ?? (disconnect.error instanceof Error ? disconnect.error.message : null);

  return (
    <li className="git-credentials-row">
      <div className="git-credentials-info">
        <div className="git-credentials-server">
          <strong>{c.server_display_name}</strong>
          <em className="git-credentials-provider">({c.server_provider_type})</em>
        </div>
        <div className="git-credentials-status">
          {c.auth_method === 'oauth'
            ? c.login_connected
              ? `✓ Connected via ${c.login_provider_name ?? 'login'}`
              : `Not connected to ${c.login_provider_name ?? 'login'}`
            : c.has_token
              ? '✓ Credential configured'
              : 'No credential'}
        </div>
        {rowError && <div className="error-message" role="alert">{rowError}</div>}
      </div>

      {c.auth_method === 'oauth' ? (
        <div className="git-credentials-actions">
          {c.login_connected ? (
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={disconnect.isPending}
              className="secondary-button secondary-button--danger"
            >
              {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => { void connect(c.login_provider_name ?? ''); }}
              disabled={isConnecting || !c.login_provider_name}
              className="secondary-button"
            >
              {isConnecting ? 'Connecting…' : 'Connect'}
            </button>
          )}
        </div>
      ) : editing ? (
        <div className="git-credentials-edit">
          <div className="form-group">
            <label htmlFor={`token-${c.server_id}`}>Personal Access Token</label>
            <div className="token-input-wrapper">
              <input
                type={showToken ? 'text' : 'password'}
                id={`token-${c.server_id}`}
                placeholder={c.has_token ? 'Enter new token to replace' : 'glpat-xxxx...'}
                value={token}
                onChange={(e) => { setToken(e.target.value); }}
              />
              <button
                type="button"
                className="toggle-visibility"
                onClick={() => { setShowToken(!showToken); }}
              >
                {showToken ? 'Hide' : 'Show'}
              </button>
            </div>
            <small className="help-text">
              Generate a token with api, read_repository, and write_repository scopes.
            </small>
          </div>
          <div className="form-group">
            <label htmlFor={`name-${c.server_id}`}>Git User Name (optional)</label>
            <input
              type="text"
              id={`name-${c.server_id}`}
              placeholder="Your Name"
              value={name}
              onChange={(e) => { setName(e.target.value); }}
            />
          </div>
          <div className="form-group">
            <label htmlFor={`email-${c.server_id}`}>Git User Email (optional)</label>
            <input
              type="email"
              id={`email-${c.server_id}`}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); }}
            />
          </div>
          {error && <div className="error-message" role="alert">{error}</div>}

          <div className="git-credentials-actions">
            <button
              type="button"
              onClick={() => void submit()}
              disabled={set.isPending}
              className="save-button"
            >
              {set.isPending ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={handleCancel} className="cancel-button">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="git-credentials-actions">
          <button type="button" onClick={handleEdit} className="secondary-button">
            {c.has_token ? 'Update credential' : 'Add credential'}
          </button>
          {c.has_token && (
            <button
              type="button"
              onClick={() => { del.mutate(c.server_id); }}
              disabled={del.isPending}
              className="secondary-button secondary-button--danger"
            >
              {del.isPending ? 'Removing…' : 'Remove'}
            </button>
          )}
        </div>
      )}
    </li>
  );
}
